import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from '../../atoms/Card/Card';
import Button from '../../atoms/Button/Button';
import { HOMELET_ENUM_OPTIONS } from '../../../constants/homeletEnums';
import './HomeletReferencingForm.scss';

const EMAIL_REGEX = /^(?=.{1,64}@)(?!.*([-+_]{2})(?!.*[-+_]\1))[+\-_.a-zA-Z0-9]([a-zA-Z0-9+\-_.]*[a-zA-Z0-9+\-_.])?(?:\.[a-zA-Z0-9]([a-zA-Z0-9+\-_.]*[a-zA-Z0-9+\-_.])?)*@((?!-)[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z0-9]{2,}$/u;
const NI_NUMBER_REGEX = /^[A-Za-z]{2}\s*\d{2}\s*\d{2}\s*\d{2}\s*[A-Za-z]?$/u;
const DECIMAL_REGEX = /^\d+(?:\.\d{1,2})?$/u;
const NAME_REGEX = /^(?![0-9!#$%&*;()~:@*/=?^{|])[\p{L}\p{Mn}\p{Pd}A-Za-z0-9][\p{L}\p{Mn}\p{Pd}A-Za-z0-9 '\-]*$/u;
const JOB_TITLE_REGEX = /^[A-Za-z ']+$/u;
const SORT_CODE_REGEX = /^\d{6}$|^\d{2}-\d{2}-\d{2}$/u;
const BANK_ACCOUNT_NUMBER_REGEX = /^\d{1,8}$/u;
const PHONE_11_DIGIT_REGEX = /^\d{11}$/u;
const PET_TYPE_OPTIONS = [
  { value: '1', label: 'Cat' },
  { value: '2', label: 'Dog' },
  { value: '3', label: 'Other' },
];
const PET_SEX_OPTIONS = [
  { value: '1', label: 'Male' },
  { value: '2', label: 'Female' },
];

const FIELD_VALIDATIONS = {
  tenancyTermMonths: { label: 'Tenancy Term Months', min: 1 },
  monthlyRental: { label: 'Monthly Rental', required: true, validator: 'decimal' },
  propertyConstructionPeriod: { label: 'Property Construction Period', min: 1, max: 9 },
  houseNumber: { label: 'House Number', maxLength: 100 },
  houseName: { label: 'House Name', maxLength: 100 },
  addressLine1: {
    label: 'Property Address Line 1', minLength: 1, maxLength: 100, required: true,
  },
  addressLine2: { label: 'Address Line 2', maxLength: 100 },
  town: {
    label: 'Property Town', minLength: 1, maxLength: 50, required: true,
  },
  postcode: {
    label: 'Property Postcode', minLength: 1, maxLength: 50, required: true,
  },
  firstName: {
    label: 'First Name', minLength: 1, maxLength: 100, required: true, validator: 'name',
  },
  middleName: { label: 'Middle Name', maxLength: 50, validator: 'name' },
  surname: {
    label: 'Surname', minLength: 1, maxLength: 100, required: true, validator: 'name',
  },
  shareOfRent: { label: 'Share Of Rent', required: true, validator: 'decimal' },
  contactTelephoneNo: {
    label: 'Contact Telephone No', minLength: 1, maxLength: 15, required: true, validator: 'phone11Digits',
  },
  emailAddress: {
    label: 'Email Address', minLength: 1, maxLength: 100, required: true, validator: 'email',
  },
  niNumber: { label: 'NI Number', maxLength: 100, validator: 'niNumber' },
  otherTitle: { label: 'Other Title', maxLength: 100, required: (values) => values.title === '6' },
  additionalInformation: { label: 'Additional Information', maxLength: 1000 },
  otherFirstName: { label: 'Other First Name', maxLength: 100, validator: 'name' },
  otherMiddleName: { label: 'Other Middle Name', maxLength: 100, validator: 'name' },
  otherLastName: { label: 'Other Last Name', maxLength: 100, validator: 'name' },
  ukAddressLine1: { label: 'UK Address Line 1', maxLength: 100, required: (values) => Boolean(values.haveYouEverLivedInUk) },
  ukTown: { label: 'UK Town', maxLength: 50, required: (values) => Boolean(values.haveYouEverLivedInUk) },
  ukPostcode: { label: 'UK Postcode', maxLength: 50, required: (values) => Boolean(values.haveYouEverLivedInUk) },
  ukMonthMovedIn: { label: 'UK Month Moved In', required: (values) => Boolean(values.haveYouEverLivedInUk) },
  ukYearMovedIn: {
    label: 'UK Year Moved In', min: 1900, max: 2026, required: (values) => Boolean(values.haveYouEverLivedInUk),
  },
  ukMonthMovedOut: { label: 'UK Month Moved Out', required: (values) => Boolean(values.haveYouEverLivedInUk) },
  ukYearMovedOut: {
    label: 'UK Year Moved Out', min: 1900, max: 2026, required: (values) => Boolean(values.haveYouEverLivedInUk),
  },
  currentRentAmount: { label: 'Current Rent Amount', validator: 'decimal' },
  landlordName: { label: 'Landlord Name', maxLength: 500 },
  landlordContactNumber: { label: 'Landlord Contact Number', maxLength: 15 },
  landlordEmail: { label: 'Landlord Email', maxLength: 200, validator: 'email' },
  otherAdverseCreditDescription: {
    label: 'Other Adverse Credit Description',
    maxLength: 1000,
    required: (values) => values.isAdverseCredit && values.adverseCreditType === 'other',
  },
  employerName: { label: 'Employer Name', maxLength: 100, required: (values) => values.incomeType === '1' },
  jobTitle: {
    label: 'Job Title', maxLength: 100, validator: 'jobTitle', required: (values) => values.incomeType === '1',
  },
  employmentGrossAnnualIncome: {
    label: 'Employment Gross Annual Income', validator: 'decimal', required: (values) => values.incomeType === '1',
  },
  employmentNetMonthlyIncome: { label: 'Employment Net Monthly Income', validator: 'decimal' },
  employmentAvgBonusIncome: { label: 'Average Bonus Income', validator: 'decimal' },
  employmentOvertimeAvgIncome: { label: 'Average Overtime Income', validator: 'decimal' },
  employmentEmployeeNumber: { label: 'Employee Number', maxLength: 100 },
  employmentRefereeName: {
    label: 'Employment Referee Name', maxLength: 100, required: (values) => values.incomeType === '1',
  },
  employmentRefereeEmail: { label: 'Employment Referee Email', maxLength: 100, validator: 'email' },
  employmentRefereePosition: {
    label: 'Employment Referee Position', maxLength: 100, required: (values) => values.incomeType === '1',
  },
  employmentRefereeContactNumber: {
    label: 'Employment Referee Contact Number', maxLength: 15, required: (values) => values.incomeType === '1',
  },
  employmentAddressLine1: { label: 'Employment Address Line 1', maxLength: 100 },
  employmentAddressLine2: { label: 'Employment Address Line 2', maxLength: 100 },
  employmentCity: { label: 'Employment City', maxLength: 100 },
  employmentPostCode: { label: 'Employment Postcode', maxLength: 100 },
  employmentTypeId: { label: 'Employment Type', required: (values) => values.incomeType === '1' },
  paymentScheduleId: { label: 'Payment Schedule', required: (values) => values.incomeType === '1' },
  startDateofEmployment: { label: 'Start Date Of Employment', required: (values) => values.incomeType === '1' },
  employmentBonusTerm: { label: 'Bonus Term' },
  employmentOvertimeTerm: { label: 'Overtime Term' },
  countryOfEmployment: { label: 'Country Of Employment' },
  tradingType: { label: 'Trading Type', required: (values) => values.incomeType === '2' },
  industryType: { label: 'Industry Type', required: (values) => values.incomeType === '1' || values.incomeType === '2' },
  startDateOfSelfEmployment: { label: 'Start Date Of Self Employment', required: (values) => values.incomeType === '2' },
  selfEmployedNetMonthlyIncome: { label: 'Self-Employed Net Monthly Income', validator: 'decimal' },
  accountantEmail: { label: 'Accountant Email', maxLength: 100, validator: 'email' },
  accountantCompanyName: { label: 'Accountant Company Name', maxLength: 500 },
  accountantContactName: { label: 'Accountant Contact Name', maxLength: 100 },
  accountantContactNumber: { label: 'Accountant Contact Number', maxLength: 500 },
  accountantContactPosition: { label: 'Accountant Contact Position', maxLength: 100 },
  accountantAddressLine1: { label: 'Accountant Address Line 1', maxLength: 100 },
  accountantAddressLine2: { label: 'Accountant Address Line 2', maxLength: 100 },
  accountantTown: { label: 'Accountant Town', maxLength: 50 },
  accountantPostcode: { label: 'Accountant Postcode', maxLength: 50 },
  pensionProvider: { label: 'Pension Provider', maxLength: 500 },
  pensionNetMonthlyIncome: { label: 'Pension Net Monthly Income', validator: 'decimal' },
  pensionGrossAnnualIncome: { label: 'Pension Gross Annual Income', required: (values) => values.incomeType === '3', validator: 'decimal' },
  studentIncomeSource: { label: 'Student Income Source', maxLength: 500 },
  studentNetMonthlyIncome: { label: 'Student Net Monthly Income', validator: 'decimal' },
  studentGrossAnnualIncome: { label: 'Student Gross Annual Income', validator: 'decimal' },
  unemployedNetMonthlyIncome: { label: 'Unemployed Net Monthly Income', validator: 'decimal' },
  unemployedGrossAnnualIncome: { label: 'Unemployed Gross Annual Income', validator: 'decimal' },
  savingsAmount: { label: 'Savings Amount', required: (values) => values.incomeType === '7', validator: 'decimal' },
  investmentScheme: { label: 'Investment Scheme / Company', maxLength: 100 },
  investmentAmount: { label: 'Investment Amount', validator: 'decimal' },
  startDateOfPension: { label: 'Start Date Of Pension', required: (values) => values.incomeType === '3' },
  studentStartDate: { label: 'Student Start Date' },
  unemployedStartDate: { label: 'Unemployed Start Date' },
  sortCode: { label: 'Sort Code', maxLength: 8, validator: 'sortCode' },
  bankAccountNumber: { label: 'Bank Account Number', maxLength: 8, validator: 'bankAccountNumber' },
  petName: { label: 'Pet Name', maxLength: 100 },
  petBreed: { label: 'Pet Breed', maxLength: 100 },
  petTypeDetail: { label: 'Pet Type Detail', maxLength: 100 },
  petAge: { label: 'Pet Age', min: 0 },
};

const getMaxDobDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 18);
  return date.toISOString().slice(0, 10);
};

const getTodayDate = () => new Date().toISOString().slice(0, 10);
const renderEnumOptions = (options = []) => options.map(({ value, label }) => (
  <option key={value} value={String(value)}>{label}</option>
));

const createAddressHistoryEntry = () => ({
  addressLine1: '',
  town: '',
  postcode: '',
  country: '2',
  monthMovedIn: '',
  yearMovedIn: '',
});

const getAddressFieldId = (index, fieldName) => `addressHistory-${index}-${fieldName}`;

const getAddressTypeValue = (index) => (index === 0 ? '1' : '2');

const getAddressTypeLabel = (index) => (index === 0 ? 'Current Address' : 'Previous Address');

const getMovedInDateParts = ({ monthMovedIn, yearMovedIn }) => {
  if (!monthMovedIn || !yearMovedIn) return null;

  const selectedMonth = Number(monthMovedIn);
  const selectedYear = Number(yearMovedIn);

  if (Number.isNaN(selectedMonth) || Number.isNaN(selectedYear)) {
    return null;
  }

  return { selectedMonth, selectedYear };
};

const validateMovedInDate = ({ monthMovedIn, yearMovedIn }, fieldId = 'monthMovedIn') => {
  const movedInDateParts = getMovedInDateParts({ monthMovedIn, yearMovedIn });
  if (!movedInDateParts) return null;

  const { selectedMonth, selectedYear } = movedInDateParts;
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  if (selectedYear > currentYear || (selectedYear === currentYear && selectedMonth > currentMonth)) {
    return {
      fieldId,
      message: 'Month moved in should not be in the future.',
    };
  }

  return null;
};

const getMonthsSince = (month, year) => {
  const movedInDateParts = getMovedInDateParts({ monthMovedIn: month, yearMovedIn: year });
  if (!movedInDateParts) return 0;

  const { selectedMonth, selectedYear } = movedInDateParts;
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  return ((currentYear - selectedYear) * 12) + (currentMonth - selectedMonth);
};

const getAddressHistoryMonths = (addressHistory = []) => {
  const completeAddresses = addressHistory.filter((address) => address.monthMovedIn && address.yearMovedIn);
  if (!completeAddresses.length) return 0;

  const oldestAddress = completeAddresses[completeAddresses.length - 1];
  return getMonthsSince(oldestAddress.monthMovedIn, oldestAddress.yearMovedIn);
};

// HomeLet (confirmed directly with their team, 2026-07-31): the 3-year address history requirement
// must be satisfied by real Addresses[] entries alone — UK Residency History is a separate,
// supplementary question and never counts towards it.
const hasEnoughAddressHistory = (addressHistory = []) => (
  getAddressHistoryMonths(addressHistory) >= 36
);

const validateAddressHistory = (addressHistory = []) => {
  if (!addressHistory.length) {
    return {
      fieldId: getAddressFieldId(0, 'addressLine1'),
      message: 'Current address is required.',
    };
  }

  for (let index = 0; index < addressHistory.length; index += 1) {
    const address = addressHistory[index];
    const addressTypeLabel = getAddressTypeLabel(index);

    if (!String(address.addressLine1 || '').trim()) {
      return {
        fieldId: getAddressFieldId(index, 'addressLine1'),
        message: `${addressTypeLabel} address line 1 is required.`,
      };
    }

    if (String(address.addressLine1 || '').trim().length > 100) {
      return {
        fieldId: getAddressFieldId(index, 'addressLine1'),
        message: `${addressTypeLabel} address line 1 must be 100 characters or fewer.`,
      };
    }

    if (!String(address.town || '').trim()) {
      return {
        fieldId: getAddressFieldId(index, 'town'),
        message: `${addressTypeLabel} town is required.`,
      };
    }

    if (String(address.town || '').trim().length > 50) {
      return {
        fieldId: getAddressFieldId(index, 'town'),
        message: `${addressTypeLabel} town must be 50 characters or fewer.`,
      };
    }

    if (!String(address.postcode || '').trim()) {
      return {
        fieldId: getAddressFieldId(index, 'postcode'),
        message: `${addressTypeLabel} postcode is required.`,
      };
    }

    if (String(address.postcode || '').trim().length > 50) {
      return {
        fieldId: getAddressFieldId(index, 'postcode'),
        message: `${addressTypeLabel} postcode must be 50 characters or fewer.`,
      };
    }

    if (!String(address.country || '').trim()) {
      return {
        fieldId: getAddressFieldId(index, 'country'),
        message: `${addressTypeLabel} country is required.`,
      };
    }

    if (!String(address.monthMovedIn || '').trim()) {
      return {
        fieldId: getAddressFieldId(index, 'monthMovedIn'),
        message: `${addressTypeLabel} month moved in is required.`,
      };
    }

    if (!String(address.yearMovedIn || '').trim()) {
      return {
        fieldId: getAddressFieldId(index, 'yearMovedIn'),
        message: `${addressTypeLabel} year moved in is required.`,
      };
    }

    const numericYear = Number(address.yearMovedIn);
    if (Number.isNaN(numericYear) || numericYear < 1900 || numericYear > 2026) {
      return {
        fieldId: getAddressFieldId(index, 'yearMovedIn'),
        message: `${addressTypeLabel} year moved in must be between 1900 and 2026.`,
      };
    }

    const movedInDateError = validateMovedInDate(address, getAddressFieldId(index, 'monthMovedIn'));
    if (movedInDateError) {
      return movedInDateError;
    }

    if (index > 0) {
      const previousAddress = addressHistory[index - 1];
      const previousDateParts = getMovedInDateParts(previousAddress);
      const currentDateParts = getMovedInDateParts(address);
      if (previousDateParts && currentDateParts) {
        const previousAddressDate = (previousDateParts.selectedYear * 100) + previousDateParts.selectedMonth;
        const currentAddressDate = (currentDateParts.selectedYear * 100) + currentDateParts.selectedMonth;

        if (currentAddressDate >= previousAddressDate) {
          return {
            fieldId: getAddressFieldId(index, 'monthMovedIn'),
            message: 'Each previous address must have an earlier moved-in date than the address above it.',
          };
        }
      }
    }
  }

  if (!hasEnoughAddressHistory(addressHistory)) {
    return {
      fieldId: getAddressFieldId(addressHistory.length - 1, 'monthMovedIn'),
      message: 'HomeLet requires at least 3 years of address history. Please add previous address history until it covers at least 3 years.',
    };
  }

  return null;
};

const normalizeAddressKey = (addressLine1, town, postcode) => (
  [addressLine1, town, postcode].map((value) => String(value || '').trim().toLowerCase()).join('|')
);

const validateUkResidencyHistory = (addressHistory = [], ukResidency = {}) => {
  if (!ukResidency.hasLivedInUk) return null;

  const ukMovedInParts = getMovedInDateParts({ monthMovedIn: ukResidency.monthMovedIn, yearMovedIn: ukResidency.yearMovedIn });
  const ukMovedOutParts = getMovedInDateParts({ monthMovedIn: ukResidency.monthMovedOut, yearMovedIn: ukResidency.yearMovedOut });

  if (ukMovedInParts && ukMovedOutParts) {
    const ukMovedInValue = (ukMovedInParts.selectedYear * 100) + ukMovedInParts.selectedMonth;
    const ukMovedOutValue = (ukMovedOutParts.selectedYear * 100) + ukMovedOutParts.selectedMonth;

    if (ukMovedOutValue <= ukMovedInValue) {
      return {
        fieldId: 'ukMonthMovedOut',
        message: 'UK Year Moved Out must be later than UK Year Moved In.',
      };
    }

    const currentAddressMovedInParts = getMovedInDateParts(addressHistory[0] || {});
    if (currentAddressMovedInParts) {
      const currentAddressMovedInValue = (currentAddressMovedInParts.selectedYear * 100) + currentAddressMovedInParts.selectedMonth;

      if (ukMovedOutValue > currentAddressMovedInValue) {
        return {
          fieldId: 'ukMonthMovedOut',
          message: 'UK Month/Year Moved Out cannot be later than when you moved into your current address, since you cannot be resident at two addresses at once.',
        };
      }
    }
  }

  const ukAddressKey = normalizeAddressKey(ukResidency.addressLine1, ukResidency.town, ukResidency.postcode);
  const isDuplicateOfHistoryAddress = ukAddressKey !== normalizeAddressKey()
    && addressHistory.some((address) => normalizeAddressKey(address.addressLine1, address.town, address.postcode) === ukAddressKey);

  if (isDuplicateOfHistoryAddress) {
    return {
      fieldId: 'ukAddressLine1',
      message: 'The UK address you entered matches an address already listed in Address History. Please check for duplicate entries.',
    };
  }

  return null;
};

const focusField = (fieldId) => {
  if (!fieldId) return;
  const field = document.getElementById(fieldId);
  if (!field) return;
  if (field.type === 'hidden') return;
  field.scrollIntoView({ behavior: 'smooth', block: 'center' });
  field.focus();
};

// Dev-only diagnostic: the tenant only ever sees "Error in Request..." text either way,
// but this lets a developer tell from the console/devtools whether a submission was blocked
// by our own client-side rules or bounced back by HomeLet's live API.
const logValidationSource = (source, checkName, message) => {
  console.info(`[HomeletReferencingForm] Blocked by ${source} (${checkName}):`, message);
};

const getRuleValue = (ruleValue, values) => (
  typeof ruleValue === 'function' ? ruleValue(values) : ruleValue
);

const runValidator = (validator, rawValue, label) => {
  const value = String(rawValue || '');

  if (validator === 'decimal' && !DECIMAL_REGEX.test(value)) {
    return `${label} can have up to 2 decimal places.`;
  }

  if (validator === 'email' && !EMAIL_REGEX.test(value)) {
    return `Enter a valid ${label.toLowerCase()}.`;
  }

  if (validator === 'niNumber') {
    if (!NI_NUMBER_REGEX.test(value.trim())) {
      return 'Enter a valid NI Number (example: QQ123456C).';
    }
  }

  if (validator === 'name' && !NAME_REGEX.test(value)) {
    return `${label} contains invalid characters.`;
  }

  if (validator === 'jobTitle' && !JOB_TITLE_REGEX.test(value)) {
    return 'Job Title can only contain letters, spaces, and apostrophes.';
  }

  if (validator === 'sortCode' && !SORT_CODE_REGEX.test(value)) {
    return 'Enter a valid sort code using 6 digits or 12-34-56 format.';
  }

  if (validator === 'bankAccountNumber' && !BANK_ACCOUNT_NUMBER_REGEX.test(value)) {
    return 'Enter a valid bank account number using up to 8 digits.';
  }

  if (validator === 'phone11Digits') {
    const digitsOnly = value.replace(/\D/g, '');
    if (!PHONE_11_DIGIT_REGEX.test(digitsOnly)) {
      return 'Contact Telephone No must contain exactly 11 digits.';
    }
  }

  return null;
};

const validateSchemaRules = (values = {}) => {
  const fieldIds = Object.keys(FIELD_VALIDATIONS);

  for (const fieldId of fieldIds) {
    const rule = FIELD_VALIDATIONS[fieldId];
    const label = rule.label || fieldId;
    const required = Boolean(getRuleValue(rule.required, values));
    const hasField = Object.prototype.hasOwnProperty.call(values, fieldId);
    const rawValue = hasField ? values[fieldId] : '';
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    const isEmpty = value === '' || value === undefined || value === null;

    if (!hasField && !required) continue;

    if (required && isEmpty) {
      return { fieldId, message: `${label} is required.` };
    }

    if (isEmpty) continue;

    if (rule.minLength !== undefined && String(value).length < rule.minLength) {
      return { fieldId, message: `${label} must be at least ${rule.minLength} characters.` };
    }

    if (rule.maxLength !== undefined && String(value).length > rule.maxLength) {
      return { fieldId, message: `${label} must be ${rule.maxLength} characters or fewer.` };
    }

    if (rule.min !== undefined || rule.max !== undefined) {
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) {
        return { fieldId, message: `${label} must be a valid number.` };
      }
      if (rule.min !== undefined && numberValue < rule.min) {
        return { fieldId, message: `${label} must be at least ${rule.min}.` };
      }
      if (rule.max !== undefined && numberValue > rule.max) {
        return { fieldId, message: `${label} must be ${rule.max} or less.` };
      }
    }

    if (rule.validator) {
      const validationMessage = runValidator(rule.validator, value, label);
      if (validationMessage) {
        return { fieldId, message: validationMessage };
      }
    }
  }

  return null;
};

const HomeletReferencingForm = ({
  onSubmit,
  submitButtonLabel,
  submitError,
  hideTenancyTerm,
  initialPropertyAddress,
  initialTenantData,
  initialRentalData,
}) => {
  const [title, setTitle] = useState('6');
  const [hasLivedInUk, setHasLivedInUk] = useState(false);
  const [ukMonthMovedIn, setUkMonthMovedIn] = useState('');
  const [ukYearMovedIn, setUkYearMovedIn] = useState('');
  const [isAdverseCredit, setIsAdverseCredit] = useState(false);
  const [hasPets, setHasPets] = useState(false);
  const [petType, setPetType] = useState('1');
  const [incomeType, setIncomeType] = useState('1');
  const [currentResidentialStatus, setCurrentResidentialStatus] = useState('2');
  const [adverseCreditType, setAdverseCreditType] = useState('');
  const [propertyAddress, setPropertyAddress] = useState(initialPropertyAddress);
  const [addressHistory, setAddressHistory] = useState([createAddressHistoryEntry()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (addressHistory?.[0]?.country === '2') {
      setHasLivedInUk(false);
    }
  }, [addressHistory]);

  useEffect(() => {
    const allAddressDatesEntered = addressHistory.every((address) => address.monthMovedIn && address.yearMovedIn);

    if (allAddressDatesEntered && !hasEnoughAddressHistory(addressHistory)) {
      setAddressHistory((currentAddressHistory) => (
        currentAddressHistory.every((address) => address.monthMovedIn && address.yearMovedIn)
          ? [...currentAddressHistory, createAddressHistoryEntry()]
          : currentAddressHistory
      ));
    }
  }, [addressHistory]);

  useEffect(() => {
    setPropertyAddress(initialPropertyAddress);
  }, [initialPropertyAddress]);

  useEffect(() => {
    if (submitError) {
      logValidationSource('API', 'HomeLet CreateReferenceWithAllData', submitError);
    }
  }, [submitError]);

  const isCurrentAddressUk = addressHistory?.[0]?.country === '2';

  const handleAddressChange = (index, fieldName, fieldValue) => {
    setAddressHistory((currentAddressHistory) => currentAddressHistory.map((address, addressIndex) => (
      addressIndex === index
        ? {
          ...address,
          [fieldName]: fieldValue,
        }
        : address
    )));
  };

  const addPreviousAddress = () => {
    setAddressHistory((currentAddressHistory) => [...currentAddressHistory, createAddressHistoryEntry()]);
  };

  const removePreviousAddress = (index) => {
    setAddressHistory((currentAddressHistory) => currentAddressHistory.filter((_, addressIndex) => addressIndex !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const values = {};

    // Collect values by element id because this form mostly uses `id` (not `name`).
    form.querySelectorAll('input, select, textarea').forEach((field) => {
      const fieldId = field?.id;
      if (!fieldId) return;

      if (field?.type === 'checkbox') {
        values[fieldId] = Boolean(field?.checked);
      } else {
        const rawFieldValue = field?.value;
        values[fieldId] = field?.type === 'hidden'
          ? (rawFieldValue ?? '')
          : (rawFieldValue?.trim?.() ?? '');
      }
    });

    values.addressHistory = addressHistory.map((address, index) => ({
      ...address,
      addressType: getAddressTypeValue(index),
      isUkAddress: address.country === '2',
    }));
    values.isAdvanceRent = Boolean(form.elements.isAdvanceRent && form.elements.isAdvanceRent.checked);
    values.validateIncome = Boolean(form.elements.validateIncome && form.elements.validateIncome.checked);
    values.isUkAddress = values.addressHistory?.[0]?.isUkAddress ?? true;
    values.haveYouEverLivedInUk = values.isUkAddress ? false : Boolean(form.elements.haveYouEverLivedInUk && form.elements.haveYouEverLivedInUk.checked);
    values.isAdverseCredit = Boolean(form.elements.isAdverseCredit && form.elements.isAdverseCredit.checked);
    values.ccj = Boolean(values.isAdverseCredit && values.adverseCreditType === 'ccj');
    values.bankruptcy = Boolean(values.isAdverseCredit && values.adverseCreditType === 'bankruptcy');
    values.insolvency = Boolean(values.isAdverseCredit && values.adverseCreditType === 'insolvency');
    values.benefitsReceived = Boolean(form.elements.benefitsReceived && form.elements.benefitsReceived.checked);
    values.universalCredit = Boolean(form.elements.universalCredit && form.elements.universalCredit.checked);

    const addressHistoryError = validateAddressHistory(values.addressHistory);
    if (addressHistoryError) {
      logValidationSource('UI', 'validateAddressHistory', addressHistoryError.message);
      setValidationError(addressHistoryError.message);
      focusField(addressHistoryError.fieldId);
      return;
    }

    const ukResidencyError = validateUkResidencyHistory(values.addressHistory, {
      hasLivedInUk: values.haveYouEverLivedInUk,
      addressLine1: values.ukAddressLine1,
      town: values.ukTown,
      postcode: values.ukPostcode,
      monthMovedIn: values.ukMonthMovedIn,
      yearMovedIn: values.ukYearMovedIn,
      monthMovedOut: values.ukMonthMovedOut,
      yearMovedOut: values.ukYearMovedOut,
    });
    if (ukResidencyError) {
      logValidationSource('UI', 'validateUkResidencyHistory', ukResidencyError.message);
      setValidationError(ukResidencyError.message);
      focusField(ukResidencyError.fieldId);
      return;
    }

    const schemaValidationError = validateSchemaRules(values);
    if (schemaValidationError) {
      logValidationSource('UI', 'validateSchemaRules', schemaValidationError.message);
      setValidationError(schemaValidationError.message);
      focusField(schemaValidationError.fieldId);
      return;
    }

    setValidationError('');
    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="homeletForm" onSubmit={handleSubmit}>
      {(validationError || submitError) && (
        <div className="homeletForm__errorBanner" role="alert">
          {validationError && <p className="homeletForm__errorBannerText" data-error-source="ui">{validationError}</p>}
          {!validationError && submitError && <p className="homeletForm__errorBannerText" data-error-source="api">{submitError}</p>}
        </div>
      )}
      <Card>
        <h2 className="homeletForm__title">Rental Info</h2>
        {!hideTenancyTerm && (
          <input
            id="tenancyTermMonths"
            type="hidden"
            value={initialRentalData.tenancyTermMonths ?? '12'}
            readOnly
          />
        )}
        <input id="propertyType" type="hidden" value="3" readOnly />
        <input id="propertyConstructionPeriod" type="hidden" value="4" readOnly />
        <input id="numberOfBedrooms" type="hidden" value="2" readOnly />
        <input id="noOfAdultOccupants" type="hidden" value="2" readOnly />
        <input id="noOfChildOccupants" type="hidden" value="0" readOnly />
        <input id="isAdvanceRent" type="hidden" value="false" readOnly />
        <input id="ukCountryCode" type="hidden" value="1" readOnly />
        <div className="homeletForm__grid homeletForm__grid--2">
          <div className="homeletForm__field">
            <label htmlFor="estimatedMoveInDate">Estimated Move-In Date *</label>
            <input id="estimatedMoveInDate" type="date" value={initialRentalData.estimatedMoveInDate ?? ''} readOnly required />
          </div>
          <div className="homeletForm__field">
            <label htmlFor="monthlyRental">Monthly Rental *</label>
            <input id="monthlyRental" type="number" step="0.01" value={initialRentalData.monthlyRental ?? ''} readOnly />
          </div>
        </div>
        <input id="houseNumber" name="houseNumber" type="hidden" value={propertyAddress.houseNumber || ''} readOnly />
        <input id="houseName" name="houseName" type="hidden" value={propertyAddress.houseName || ''} readOnly />
        <input id="addressLine1" name="addressLine1" type="hidden" value={propertyAddress.addressLine1 || ''} readOnly />
        <input id="addressLine2" name="addressLine2" type="hidden" value={propertyAddress.addressLine2 || ''} readOnly />
        <input id="town" name="town" type="hidden" value={propertyAddress.town || ''} readOnly />
        <input id="postcode" name="postcode" type="hidden" value={propertyAddress.postcode || ''} readOnly />
      </Card>

      <Card>
        <h2 className="homeletForm__title">Tenant</h2>
        <div className="homeletForm__grid homeletForm__grid--2">
          <div className="homeletForm__field">
            <label htmlFor="tenantTypeId">Tenant Type *</label>
            <select id="tenantTypeId" defaultValue={initialTenantData.tenantTypeId || '1'} required>
              {renderEnumOptions(HOMELET_ENUM_OPTIONS.PropertyTenantType)}
            </select>
          </div>
        </div>
        <div className="homeletForm__grid homeletForm__grid--3">
          <div className="homeletForm__field">
            <label htmlFor="title">Title *</label>
            <select id="title" value={title} onChange={(e) => setTitle(e.target.value)} required>
              {renderEnumOptions(HOMELET_ENUM_OPTIONS.Title)}
            </select>
          </div>
          {title === '6' && (
            <div className="homeletForm__field">
              <label htmlFor="otherTitle">Other Title</label>
              <input id="otherTitle" type="text" maxLength="100" />
            </div>
          )}
          <div className="homeletForm__field">
            <label htmlFor="firstName">First Name *</label>
            <input id="firstName" type="text" required defaultValue={initialTenantData.firstName || ''} />
          </div>
        </div>
        <div className="homeletForm__grid homeletForm__grid--3">
          <div className="homeletForm__field">
            <label htmlFor="middleName">Middle Name</label>
            <input id="middleName" type="text" />
          </div>
          <div className="homeletForm__field">
            <label htmlFor="surname">Surname *</label>
            <input id="surname" type="text" required defaultValue={initialTenantData.surname || ''} />
          </div>
          <div className="homeletForm__field">
            <label htmlFor="shareOfRent">Share Of Rent *</label>
            <input id="shareOfRent" type="number" step="0.01" defaultValue={initialRentalData.shareOfRent ?? initialRentalData.monthlyRental ?? ''} required />
          </div>
        </div>
        <div className="homeletForm__grid homeletForm__grid--3">
          <div className="homeletForm__field">
            <label htmlFor="telephoneCountryId">Telephone Country ID *</label>
            <select id="telephoneCountryId" defaultValue="2" required>
              {renderEnumOptions(HOMELET_ENUM_OPTIONS.CountryEnum)}
            </select>
          </div>
          <div className="homeletForm__field">
            <label htmlFor="contactTelephoneNo">Contact Telephone No *</label>
            <input id="contactTelephoneNo" type="text" maxLength="15" required defaultValue={initialTenantData.contactTelephoneNo || ''} />
          </div>
          <div className="homeletForm__field">
            <label htmlFor="emailAddress">Email Address *</label>
            <input id="emailAddress" type="email" required defaultValue={initialTenantData.emailAddress || ''} />
          </div>
        </div>
        <div className="homeletForm__grid homeletForm__grid--3">
          <div className="homeletForm__field">
            <label htmlFor="dateOfBirth">Date Of Birth *</label>
            <input id="dateOfBirth" type="date" max={getMaxDobDate()} required defaultValue={initialTenantData.dateOfBirth || ''} />
          </div>
          <div className="homeletForm__field">
            <label htmlFor="nationality">Nationality *</label>
            <select id="nationality" defaultValue="28" required>
              {renderEnumOptions(HOMELET_ENUM_OPTIONS.Nationality)}
            </select>
          </div>
          <div className="homeletForm__field">
            <label htmlFor="niNumber">NI Number</label>
            <input
              id="niNumber"
              type="text"
              maxLength="100"
            />
          </div>
        </div>
        <div className="homeletForm__grid homeletForm__grid--2">
          <div className="homeletForm__field">
            <label htmlFor="residentStatus">Resident Status *</label>
            <select id="residentStatus" defaultValue="1" required>
              {renderEnumOptions(HOMELET_ENUM_OPTIONS.PersonalResidentStatus)}
            </select>
          </div>
          <div className="homeletForm__field">
            <label htmlFor="additionalInformation">Additional Information</label>
            <textarea id="additionalInformation" placeholder="Add any extra context you want Grainger or HomeLet to know about this application." />
          </div>
        </div>
        <h3 className="homeletForm__subtitle">Other Names</h3>
        <div className="homeletForm__grid homeletForm__grid--3">
          <div className="homeletForm__field">
            <label htmlFor="otherFirstName">First Name</label>
            <input id="otherFirstName" type="text" />
          </div>
          <div className="homeletForm__field">
            <label htmlFor="otherMiddleName">Middle Name</label>
            <input id="otherMiddleName" type="text" />
          </div>
          <div className="homeletForm__field">
            <label htmlFor="otherLastName">Last Name</label>
            <input id="otherLastName" type="text" />
          </div>
        </div>
        <div className="homeletForm__grid homeletForm__grid--2">
          <div className="homeletForm__field">
            <label htmlFor="nameChangeDate">Name Change Date</label>
            <input id="nameChangeDate" type="date" />
          </div>
        </div>

        <h3 className="homeletForm__subtitle">Address History</h3>
        {addressHistory.map((address, index) => {
          const isCurrentAddress = index === 0;

          return (
            <React.Fragment key={getAddressFieldId(index, 'section')}>
              <div className="homeletForm__addressSection">
                <div className="homeletForm__addressSectionHeader">
                  <div>
                    <h4 className="homeletForm__addressSectionTitle">{getAddressTypeLabel(index)}</h4>
                    <p className="homeletForm__addressSectionHelp">
                      {isCurrentAddress
                        ? 'Start with your current address. If you have lived there for less than 3 years, we will ask for previous addresses too.'
                        : 'Add the address you lived at before the one above.'}
                    </p>
                  </div>
                  {!isCurrentAddress && (
                  <button
                    type="button"
                    className="homeletForm__addressSectionAction"
                    onClick={() => removePreviousAddress(index)}
                  >
                    Remove
                  </button>
                  )}
                </div>
                <input
                  id={getAddressFieldId(index, 'isUkAddress')}
                  type="hidden"
                  value={address.country === '2' ? 'true' : 'false'}
                  readOnly
                />
                <div className="homeletForm__grid homeletForm__grid--3">
                  <div className="homeletForm__field">
                    <label htmlFor={getAddressFieldId(index, 'addressLine1')}>Address Line 1 *</label>
                    <input
                      id={getAddressFieldId(index, 'addressLine1')}
                      type="text"
                      maxLength="100"
                      value={address.addressLine1}
                      onChange={(event) => handleAddressChange(index, 'addressLine1', event.target.value)}
                      required
                    />
                  </div>
                  <div className="homeletForm__field">
                    <label htmlFor={getAddressFieldId(index, 'town')}>Town *</label>
                    <input
                      id={getAddressFieldId(index, 'town')}
                      type="text"
                      maxLength="50"
                      value={address.town}
                      onChange={(event) => handleAddressChange(index, 'town', event.target.value)}
                      required
                    />
                  </div>
                  <div className="homeletForm__field">
                    <label htmlFor={getAddressFieldId(index, 'postcode')}>Postcode *</label>
                    <input
                      id={getAddressFieldId(index, 'postcode')}
                      type="text"
                      maxLength="50"
                      value={address.postcode}
                      onChange={(event) => handleAddressChange(index, 'postcode', event.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="homeletForm__grid homeletForm__grid--3">
                  <div className="homeletForm__field">
                    <label htmlFor={getAddressFieldId(index, 'country')}>Country *</label>
                    <select
                      id={getAddressFieldId(index, 'country')}
                      value={address.country}
                      onChange={(event) => handleAddressChange(index, 'country', event.target.value)}
                      required
                    >
                      <option value="" disabled>Select country</option>
                      {renderEnumOptions(HOMELET_ENUM_OPTIONS.CountryEnum)}
                    </select>
                  </div>
                  <div className="homeletForm__field">
                    <label htmlFor={getAddressFieldId(index, 'addressType')}>Address Type *</label>
                    <input
                      id={getAddressFieldId(index, 'addressType')}
                      type="text"
                      value={getAddressTypeLabel(index)}
                      readOnly
                    />
                  </div>
                </div>
                <div className="homeletForm__grid homeletForm__grid--3">
                  <div className="homeletForm__field">
                    <label htmlFor={getAddressFieldId(index, 'monthMovedIn')}>Month Moved In *</label>
                    <select
                      id={getAddressFieldId(index, 'monthMovedIn')}
                      value={address.monthMovedIn}
                      onChange={(event) => handleAddressChange(index, 'monthMovedIn', event.target.value)}
                      required
                    >
                      <option value="" disabled>Select month</option>
                      {renderEnumOptions(HOMELET_ENUM_OPTIONS.Months)}
                    </select>
                  </div>
                  <div className="homeletForm__field">
                    <label htmlFor={getAddressFieldId(index, 'yearMovedIn')}>Year Moved In *</label>
                    <input
                      id={getAddressFieldId(index, 'yearMovedIn')}
                      type="number"
                      min="1900"
                      max="2026"
                      value={address.yearMovedIn}
                      onChange={(event) => handleAddressChange(index, 'yearMovedIn', event.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>
              {isCurrentAddress && (
              <div className={`homeletForm__addressSection${isCurrentAddressUk ? ' homeletForm__addressSection--disabled' : ''}`}>
                <div className="homeletForm__addressSectionHeader">
                  <div>
                    <h4 className="homeletForm__addressSectionTitle">UK Residency History</h4>
                    <p className="homeletForm__addressSectionHelp">
                      {isCurrentAddressUk
                        ? 'This only applies if your current address is outside the UK.'
                        : 'Your current address is outside the UK. Let us know if you have ever lived in the UK before, so HomeLet can reference that address too.'}
                    </p>
                  </div>
                </div>
                <div className="homeletForm__grid homeletForm__grid--2">
                  <div className="homeletForm__field homeletForm__field--checkbox">
                    <input
                      id="haveYouEverLivedInUk"
                      type="checkbox"
                      checked={hasLivedInUk}
                      disabled={isCurrentAddressUk}
                      onChange={(e) => setHasLivedInUk(e.target.checked)}
                    />
                    <label htmlFor="haveYouEverLivedInUk">Have you ever lived in the UK?</label>
                  </div>
                </div>
                {!isCurrentAddressUk && hasLivedInUk && (
                  <>
                    <div className="homeletForm__grid homeletForm__grid--3">
                      <div className="homeletForm__field">
                        <label htmlFor="ukAddressLine1">UK Address Line 1 *</label>
                        <input id="ukAddressLine1" type="text" required />
                      </div>
                      <div className="homeletForm__field">
                        <label htmlFor="ukTown">UK Town *</label>
                        <input id="ukTown" type="text" required />
                      </div>
                      <div className="homeletForm__field">
                        <label htmlFor="ukPostcode">UK Postcode *</label>
                        <input id="ukPostcode" type="text" required />
                      </div>
                    </div>
                    <div className="homeletForm__grid homeletForm__grid--2">
                      <div className="homeletForm__field">
                        <label htmlFor="ukMonthMovedIn">UK Month Moved In *</label>
                        <select id="ukMonthMovedIn" value={ukMonthMovedIn} onChange={(e) => setUkMonthMovedIn(e.target.value)} required>
                          <option value="" disabled>Select month</option>
                          {renderEnumOptions(HOMELET_ENUM_OPTIONS.Months)}
                        </select>
                      </div>
                      <div className="homeletForm__field">
                        <label htmlFor="ukYearMovedIn">UK Year Moved In *</label>
                        <input id="ukYearMovedIn" type="number" min="1900" max="2026" value={ukYearMovedIn} onChange={(e) => setUkYearMovedIn(e.target.value)} required />
                      </div>
                    </div>
                    <div className="homeletForm__grid homeletForm__grid--2">
                      <div className="homeletForm__field">
                        <label htmlFor="ukMonthMovedOut">UK Month Moved Out *</label>
                        <select id="ukMonthMovedOut" defaultValue="" required>
                          <option value="" disabled>Select month</option>
                          {renderEnumOptions(HOMELET_ENUM_OPTIONS.Months)}
                        </select>
                      </div>
                      <div className="homeletForm__field">
                        <label htmlFor="ukYearMovedOut">UK Year Moved Out *</label>
                        <input id="ukYearMovedOut" type="number" min="1900" max="2026" required />
                      </div>
                    </div>
                  </>
                )}
              </div>
              )}
            </React.Fragment>
          );
        })}
        <div className="homeletForm__addressHistoryActions">
          <button type="button" className="homeletForm__addressSectionAction" onClick={addPreviousAddress}>
            Add Previous Address
          </button>
          <p className="homeletForm__addressHistoryHint">
            Minimum 3 years of address history is required. Previous address sections will appear automatically until it's covered.
          </p>
        </div>
        <h3 className="homeletForm__subtitle">Residential + Adverse Credit</h3>
        <div className="homeletForm__grid homeletForm__grid--3">
          <div className="homeletForm__field">
            <label htmlFor="currentResidentialStatus">Current Residential Status *</label>
            <select
              id="currentResidentialStatus"
              value={currentResidentialStatus}
              onChange={(e) => setCurrentResidentialStatus(e.target.value)}
              required
            >
              {renderEnumOptions(HOMELET_ENUM_OPTIONS.ResidentialStatus)}
            </select>
          </div>
        </div>
        {currentResidentialStatus === '2' && (
          <>
            <div className="homeletForm__grid homeletForm__grid--3">
              <div className="homeletForm__field">
                <label htmlFor="currentRentAmount">Current Rent Amount</label>
                <input id="currentRentAmount" type="number" step="0.01" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="landlordName">Landlord Name</label>
                <input id="landlordName" type="text" maxLength="500" />
              </div>
            </div>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field">
                <label htmlFor="landlordContactNumber">Landlord Contact Number</label>
                <input id="landlordContactNumber" type="text" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="landlordEmail">Landlord Email</label>
                <input id="landlordEmail" type="email" />
              </div>
            </div>
          </>
        )}
        <div className="homeletForm__grid homeletForm__grid--2">
          <div className="homeletForm__field homeletForm__field--checkbox">
            <input
              id="isAdverseCredit"
              type="checkbox"
              checked={isAdverseCredit}
              onChange={(e) => {
                const { checked } = e.target;
                setIsAdverseCredit(checked);
                if (!checked) {
                  setAdverseCreditType('');
                }
              }}
            />
            <label htmlFor="isAdverseCredit">Do you have adverse credit?</label>
          </div>
        </div>
        {isAdverseCredit && (
          <>
            <div className="homeletForm__adverseCreditSection">
              <div className="homeletForm__inlineNote homeletForm__inlineNote--compact" role="note">
                Select the option that best describes the adverse credit history.
              </div>
              <input id="adverseCreditType" type="hidden" value={adverseCreditType} readOnly />
              <div className="homeletForm__radioGroup" role="radiogroup" aria-label="Adverse credit type">
                <label className="homeletForm__radioOption" htmlFor="adverseCreditType-ccj">
                  <input
                    id="adverseCreditType-ccj"
                    type="radio"
                    name="adverseCreditTypeChoice"
                    checked={adverseCreditType === 'ccj'}
                    onChange={() => setAdverseCreditType('ccj')}
                  />
                  <span>CCJ</span>
                </label>
                <label className="homeletForm__radioOption" htmlFor="adverseCreditType-bankruptcy">
                  <input
                    id="adverseCreditType-bankruptcy"
                    type="radio"
                    name="adverseCreditTypeChoice"
                    checked={adverseCreditType === 'bankruptcy'}
                    onChange={() => setAdverseCreditType('bankruptcy')}
                  />
                  <span>Bankruptcy</span>
                </label>
                <label className="homeletForm__radioOption" htmlFor="adverseCreditType-insolvency">
                  <input
                    id="adverseCreditType-insolvency"
                    type="radio"
                    name="adverseCreditTypeChoice"
                    checked={adverseCreditType === 'insolvency'}
                    onChange={() => setAdverseCreditType('insolvency')}
                  />
                  <span>Insolvency</span>
                </label>
                <label className="homeletForm__radioOption" htmlFor="adverseCreditType-other">
                  <input
                    id="adverseCreditType-other"
                    type="radio"
                    name="adverseCreditTypeChoice"
                    checked={adverseCreditType === 'other'}
                    onChange={() => setAdverseCreditType('other')}
                  />
                  <span>Other</span>
                </label>
              </div>
            </div>
            {adverseCreditType === 'other' && (
              <div className="homeletForm__grid homeletForm__grid--2">
                <div className="homeletForm__field">
                  <label htmlFor="otherAdverseCreditDescription">Other Adverse Credit Description *</label>
                  <textarea id="otherAdverseCreditDescription" />
                </div>
              </div>
            )}
          </>
        )}

        <h3 className="homeletForm__subtitle">Income</h3>
        <div className="homeletForm__grid homeletForm__grid--3">
          <div className="homeletForm__field">
            <label htmlFor="incomeType">Income Type *</label>
            <select id="incomeType" value={incomeType} onChange={(e) => setIncomeType(e.target.value)} required>
              {renderEnumOptions(HOMELET_ENUM_OPTIONS.IncomeType)}
            </select>
          </div>
          <div className="homeletForm__field">
            <label htmlFor="incomeTerm">Income Term *</label>
            <select id="incomeTerm" defaultValue="1" required>
              {renderEnumOptions(HOMELET_ENUM_OPTIONS.IncomeTerm)}
            </select>
          </div>
        </div>
        <div className="homeletForm__grid homeletForm__grid--2">
          <div className="homeletForm__field">
            <label htmlFor="sortCode">Sort Code</label>
            <input id="sortCode" type="text" maxLength="8" />
          </div>
          <div className="homeletForm__field">
            <label htmlFor="bankAccountNumber">Bank Account Number</label>
            <input id="bankAccountNumber" type="text" maxLength="8" />
          </div>
        </div>

        {incomeType === '1' && (
          <>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field">
                <label htmlFor="employerName">Employer Name *</label>
                <input id="employerName" type="text" required />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="jobTitle">Job Title *</label>
                <input id="jobTitle" type="text" required />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentGrossAnnualIncome">Employment Gross Annual Income *</label>
                <input id="employmentGrossAnnualIncome" type="number" step="0.01" required />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentNetMonthlyIncome">Employment Net Monthly Income</label>
                <input id="employmentNetMonthlyIncome" type="number" step="0.01" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="industryType">Industry Type *</label>
                <select id="industryType" defaultValue="" required>
                  <option value="" disabled>Select industry type</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.IndustryType)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentTypeId">Employment Type *</label>
                <select id="employmentTypeId" defaultValue="" required>
                  <option value="" disabled>Select employment type</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.TypeOfEmployment)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="paymentScheduleId">Payment Schedule *</label>
                <select id="paymentScheduleId" defaultValue="" required>
                  <option value="" disabled>Select payment schedule</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.BonusReceived)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="countryOfEmployment">Country Of Employment</label>
                <select id="countryOfEmployment" defaultValue="2">
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.CountryEnum)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="startDateofEmployment">Start Date Of Employment *</label>
                <input id="startDateofEmployment" type="date" required />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentEmployeeNumber">Employee Number</label>
                <input id="employmentEmployeeNumber" type="text" maxLength="100" />
              </div>
            </div>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="employmentZeroHourContract" type="checkbox" />
                <label htmlFor="employmentZeroHourContract">Zero Hour Contract</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="employmentBonuses" type="checkbox" />
                <label htmlFor="employmentBonuses">Receives Bonuses</label>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentBonusTerm">Bonus Term</label>
                <select id="employmentBonusTerm" defaultValue="">
                  <option value="">Select bonus term</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.PaidTermType)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentAvgBonusIncome">Average Bonus Income</label>
                <input id="employmentAvgBonusIncome" type="number" step="0.01" />
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="employmentOvertimeReceived" type="checkbox" />
                <label htmlFor="employmentOvertimeReceived">Receives Overtime</label>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentOvertimeTerm">Overtime Term</label>
                <select id="employmentOvertimeTerm" defaultValue="">
                  <option value="">Select overtime term</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.PaidTermType)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentOvertimeAvgIncome">Average Overtime Income</label>
                <input id="employmentOvertimeAvgIncome" type="number" step="0.01" />
              </div>
            </div>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field">
                <label htmlFor="employmentRefereeName">Employment Referee Name *</label>
                <input id="employmentRefereeName" type="text" maxLength="100" required />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentRefereePosition">Employment Referee Position *</label>
                <input id="employmentRefereePosition" type="text" maxLength="100" required />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentRefereeContactCountryID">Employment Referee Country</label>
                <select id="employmentRefereeContactCountryID" defaultValue="">
                  <option value="">Select country</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.CountryEnum)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentRefereeContactNumber">Employment Referee Contact Number *</label>
                <input id="employmentRefereeContactNumber" type="text" maxLength="15" required />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentRefereeEmail">Employment Referee Email</label>
                <input id="employmentRefereeEmail" type="email" />
              </div>
            </div>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field">
                <label htmlFor="employmentAddressLine1">Employment Address Line 1</label>
                <input id="employmentAddressLine1" type="text" maxLength="100" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentAddressLine2">Employment Address Line 2</label>
                <input id="employmentAddressLine2" type="text" maxLength="100" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentCity">Employment City</label>
                <input id="employmentCity" type="text" maxLength="100" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="employmentPostCode">Employment Postcode</label>
                <input id="employmentPostCode" type="text" maxLength="100" />
              </div>
            </div>
          </>
        )}
        {incomeType === '2' && (
          <>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field">
                <label htmlFor="tradingType">Trading Type *</label>
                <select id="tradingType" defaultValue="" required>
                  <option value="" disabled>Select trading type</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.SelfEmployedTradingType)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="industryType">Industry Type *</label>
                <select id="industryType" defaultValue="" required>
                  <option value="" disabled>Select industry type</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.IndustryType)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="startDateOfSelfEmployment">Start Date Of Self Employment *</label>
                <input id="startDateOfSelfEmployment" type="date" max={getTodayDate()} required />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="selfEmployedGrossAnnualIncome">Self-Employed Gross Annual Income</label>
                <input id="selfEmployedGrossAnnualIncome" type="number" step="0.01" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="selfEmployedNetMonthlyIncome">Self-Employed Net Monthly Income</label>
                <input id="selfEmployedNetMonthlyIncome" type="number" step="0.01" />
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="taxReturnSubmitted" type="checkbox" />
                <label htmlFor="taxReturnSubmitted">Tax Return Submitted</label>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="usesAccountant">Uses Accountant</label>
                <select id="usesAccountant" defaultValue="No">
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field">
                <label htmlFor="accountantCompanyName">Accountant Company Name</label>
                <input id="accountantCompanyName" type="text" maxLength="500" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantContactName">Accountant Contact Name</label>
                <input id="accountantContactName" type="text" maxLength="100" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantContactNoCountryId">Accountant Contact Country</label>
                <select id="accountantContactNoCountryId" defaultValue="">
                  <option value="">Select country</option>
                  {renderEnumOptions(HOMELET_ENUM_OPTIONS.CountryEnum)}
                </select>
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantContactNumber">Accountant Contact Number</label>
                <input id="accountantContactNumber" type="text" maxLength="500" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantEmail">Accountant Email</label>
                <input id="accountantEmail" type="email" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantContactPosition">Accountant Contact Position</label>
                <input id="accountantContactPosition" type="text" maxLength="100" />
              </div>
            </div>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field">
                <label htmlFor="accountantHouseNumber">Accountant House Number</label>
                <input id="accountantHouseNumber" type="text" maxLength="100" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantHouseName">Accountant House Name</label>
                <input id="accountantHouseName" type="text" maxLength="100" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantAddressLine1">Accountant Address Line 1</label>
                <input id="accountantAddressLine1" type="text" maxLength="100" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantAddressLine2">Accountant Address Line 2</label>
                <input id="accountantAddressLine2" type="text" maxLength="100" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantTown">Accountant Town</label>
                <input id="accountantTown" type="text" maxLength="50" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="accountantPostcode">Accountant Postcode</label>
                <input id="accountantPostcode" type="text" maxLength="50" />
              </div>
            </div>
          </>
        )}
        {incomeType === '3' && (
          <div className="homeletForm__grid homeletForm__grid--2">
            <div className="homeletForm__field">
              <label htmlFor="pensionProvider">Pension Provider</label>
              <input id="pensionProvider" type="text" />
            </div>
            <div className="homeletForm__field">
              <label htmlFor="pensionNetMonthlyIncome">Pension Net Monthly Income</label>
              <input id="pensionNetMonthlyIncome" type="number" step="0.01" />
            </div>
            <div className="homeletForm__field">
              <label htmlFor="pensionGrossAnnualIncome">Pension Gross Annual Income *</label>
              <input id="pensionGrossAnnualIncome" type="number" step="0.01" required />
            </div>
            <div className="homeletForm__field">
              <label htmlFor="startDateOfPension">Start Date Of Pension *</label>
              <input id="startDateOfPension" type="date" max={getTodayDate()} required />
            </div>
          </div>
        )}
        {incomeType === '5' && (
          <div className="homeletForm__grid homeletForm__grid--2">
            <div className="homeletForm__field">
              <label htmlFor="studentIncomeSource">Student Income Source</label>
              <input id="studentIncomeSource" type="text" />
            </div>
            <div className="homeletForm__field">
              <label htmlFor="studentNetMonthlyIncome">Student Net Monthly Income</label>
              <input id="studentNetMonthlyIncome" type="number" step="0.01" />
            </div>
            <div className="homeletForm__field">
              <label htmlFor="studentGrossAnnualIncome">Student Gross Annual Income</label>
              <input id="studentGrossAnnualIncome" type="number" step="0.01" />
            </div>
            <div className="homeletForm__field">
              <label htmlFor="studentStartDate">Student Start Date</label>
              <input id="studentStartDate" type="date" />
            </div>
          </div>
        )}
        {incomeType === '6' && (
          <>
            <div className="homeletForm__grid homeletForm__grid--2">
              <div className="homeletForm__field">
                <label htmlFor="unemployedNetMonthlyIncome">Unemployed Net Monthly Income</label>
                <input id="unemployedNetMonthlyIncome" type="number" step="0.01" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="unemployedGrossAnnualIncome">Unemployed Gross Annual Income</label>
                <input id="unemployedGrossAnnualIncome" type="number" step="0.01" />
              </div>
              <div className="homeletForm__field">
                <label htmlFor="unemployedStartDate">Unemployed Start Date</label>
                <input id="unemployedStartDate" type="date" max={getTodayDate()} />
              </div>
            </div>
            <div className="homeletForm__grid homeletForm__grid--3">
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="benefitsReceived" type="checkbox" />
                <label htmlFor="benefitsReceived">Benefits Received</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="universalCredit" type="checkbox" />
                <label htmlFor="universalCredit">Universal Credit</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="taxCredit" type="checkbox" />
                <label htmlFor="taxCredit">Tax Credit</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="carerAllowance" type="checkbox" />
                <label htmlFor="carerAllowance">Carer Allowance</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="childBenefit" type="checkbox" />
                <label htmlFor="childBenefit">Child Benefit</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="disabilityBenefit" type="checkbox" />
                <label htmlFor="disabilityBenefit">Disability Benefit</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="fosterAllowance" type="checkbox" />
                <label htmlFor="fosterAllowance">Foster Allowance</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="guardianAllowance" type="checkbox" />
                <label htmlFor="guardianAllowance">Guardian Allowance</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="childMaintenance" type="checkbox" />
                <label htmlFor="childMaintenance">Child Maintenance</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="housingBenefit" type="checkbox" />
                <label htmlFor="housingBenefit">Housing Benefit</label>
              </div>
              <div className="homeletForm__field homeletForm__field--checkbox">
                <input id="employmentSupport" type="checkbox" />
                <label htmlFor="employmentSupport">Employment Support</label>
              </div>
            </div>
          </>
        )}
        {incomeType === '7' && (
          <div className="homeletForm__grid homeletForm__grid--2">
            <div className="homeletForm__field">
              <label htmlFor="savingsAmount">Savings Amount *</label>
              <input id="savingsAmount" type="number" step="1" />
            </div>
          </div>
        )}
        {incomeType === '8' && (
          <div className="homeletForm__grid homeletForm__grid--2">
            <div className="homeletForm__field">
              <label htmlFor="investmentScheme">Investment Scheme / Company</label>
              <input id="investmentScheme" type="text" />
            </div>
            <div className="homeletForm__field">
              <label htmlFor="investmentAmount">Investment Amount</label>
              <input id="investmentAmount" type="number" step="0.01" />
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div className="homeletForm__petSection">
          <div className="homeletForm__petSectionHeader">
            <h3 className="homeletForm__petTitle">Pets At The Property</h3>
            <p className="homeletForm__petHelp">Let us know if you are bringing any pets to live with you in the property.</p>
          </div>
          <div className="homeletForm__grid homeletForm__grid--2">
            <div className="homeletForm__field homeletForm__field--checkbox homeletForm__field--checkboxCard">
              <input
                id="pet"
                type="checkbox"
                checked={hasPets}
                onChange={(e) => setHasPets(e.target.checked)}
              />
              <label htmlFor="pet">This tenant has pets or plans to keep pets</label>
            </div>
            <div className={`homeletForm__petDetails${hasPets ? '' : ' homeletForm__petDetails--disabled'}`}>
              <h4 className="homeletForm__petDetailsTitle">Pet Details</h4>
              <div className="homeletForm__grid homeletForm__grid--5">
                <div className="homeletForm__field">
                  <label htmlFor="petType">Type</label>
                  <select id="petType" value={petType} onChange={(e) => setPetType(e.target.value)} disabled={!hasPets}>
                    {renderEnumOptions(PET_TYPE_OPTIONS)}
                  </select>
                </div>
                <div className="homeletForm__field">
                  <label htmlFor="petName">Name</label>
                  <input id="petName" type="text" maxLength="100" disabled={!hasPets} />
                </div>
                <div className="homeletForm__field">
                  <label htmlFor="petBreed">Breed</label>
                  <input id="petBreed" type="text" maxLength="100" disabled={!hasPets} />
                </div>
                <div className="homeletForm__field">
                  <label htmlFor="petAge">Age</label>
                  <input id="petAge" type="number" min="0" disabled={!hasPets} />
                </div>
                <div className="homeletForm__field">
                  <label htmlFor="petSex">Sex</label>
                  <select id="petSex" defaultValue="1" disabled={!hasPets}>
                    {renderEnumOptions(PET_SEX_OPTIONS)}
                  </select>
                </div>
              </div>
              {petType === '3' && (
                <div className="homeletForm__grid homeletForm__grid--2">
                  <div className="homeletForm__field">
                    <label htmlFor="petTypeDetail">Type Detail</label>
                    <input id="petTypeDetail" type="text" maxLength="100" disabled={!hasPets} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <p className="homeletForm__consentNote">
          By providing your details below, you agree that we may share your information with HomeLet, our third-party referencing provider, who will carry out referencing checks on our behalf. Your information will be handled in accordance with applicable data protection laws and used solely for the purposes of completing your referencing.
        </p>
      </Card>

      <div className="homeletForm__actions">
        {validationError && <p className="homeletForm__submitError" data-error-source="ui">{validationError}</p>}
        {submitError && <p className="homeletForm__submitError" data-error-source="api">{submitError}</p>}
        <Button type="submit" title={isSubmitting ? 'Submitting...' : submitButtonLabel} disabled={isSubmitting} />
      </div>
    </form>
  );
};

HomeletReferencingForm.propTypes = {
  onSubmit: PropTypes.func,
  submitButtonLabel: PropTypes.string,
  submitError: PropTypes.string,
  hideTenancyTerm: PropTypes.bool,
  initialPropertyAddress: PropTypes.shape({
    houseNumber: PropTypes.string,
    houseName: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    town: PropTypes.string,
    postcode: PropTypes.string,
  }),
  initialTenantData: PropTypes.shape({
    tenantTypeId: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    firstName: PropTypes.string,
    surname: PropTypes.string,
    emailAddress: PropTypes.string,
    contactTelephoneNo: PropTypes.string,
    dateOfBirth: PropTypes.string,
  }),
  initialRentalData: PropTypes.shape({
    tenancyTermMonths: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    monthlyRental: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    shareOfRent: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    estimatedMoveInDate: PropTypes.string,
  }),
};

HomeletReferencingForm.defaultProps = {
  onSubmit: () => {},
  submitButtonLabel: 'Continue',
  submitError: '',
  hideTenancyTerm: false,
  initialPropertyAddress: {},
  initialTenantData: {},
  initialRentalData: {},
};

export default HomeletReferencingForm;
export {
  getAddressHistoryMonths,
  hasEnoughAddressHistory,
  validateAddressHistory,
  validateUkResidencyHistory,
};
